
import { JSX } from 'react';
import { PlayerOverview } from '../../../types/player';
import { getFormIcon, getPlayingTimeIcon } from '../../../utils/iconUtils';
import { IndexCell, PlayerTableAgeCell, PlayerTableContractCell, PositionCell } from '../tableCells';


interface PlayerTableColumnCellProps {
  row: PlayerOverview;
  language?: string;
  positionGroup?: string;
}


interface PlayerTableColumn {
  key: string;
  width: number;
  headerPaddingRight?: number;

  orderByKey?: string;
  roleId?: string;

  cell: (props: PlayerTableColumnCellProps) => JSX.Element;
}


const playerTableInfoColumnsWidth = 270;
const playerTableStaticOverallDataColumnsWidth = 260;

export const playerTablePadding = 15;
export const playerTableStaticColumnsWidth = playerTableInfoColumnsWidth + playerTableStaticOverallDataColumnsWidth + playerTablePadding;
export const playerTableOverallIndexColumnWidth = 100;
export const playerTableRoleIndexColumnWidth = 110;

export const playerTableInfoColumns: PlayerTableColumn[] = [
  {
    key: 'age',
    width: 90,
    orderByKey: 'age',
    cell: ({ row, language }: PlayerTableColumnCellProps) => (
      <PlayerTableAgeCell row={row} language={language} />
    ),
  },
  {
    key: 'contract',
    width: 90,
    cell: ({ row, language }: PlayerTableColumnCellProps) => (
      <PlayerTableContractCell row={row} language={language} />
    ),
  },
  {
    key: 'position',
    width: 90,
    cell: ({ row, language }: PlayerTableColumnCellProps) => (
      <PositionCell row={row} language={language} />
    ),
  },
];


export const getPlayerTableOverallDataColumns = (
  anyRoleIsSelected: boolean,
  hasSkillcorner: boolean,
  showIndexConfidences: boolean,
) => {

  // if a role is selcected, none of the overall columns will be the last column
  // if no role is selected, the last column will be the physical index column if it exists, otherwise the skill index column
  const skillIndexPaddingRight = !anyRoleIsSelected && !hasSkillcorner ? playerTablePadding : undefined;
  const physicalIndexPaddingRight = !anyRoleIsSelected && hasSkillcorner ? playerTablePadding : undefined;

  const dataColumns: PlayerTableColumn[] = [
    {
      key: 'form',
      width: 75,
      cell: ({ row }: PlayerTableColumnCellProps) => (
        <div className='table-cell'>
          {getFormIcon(row.form_status, 18, 'dark', 43)}
        </div>
      ),
    },
    {
      key: 'playingTime',
      width: 85,
      cell: ({ row }: PlayerTableColumnCellProps) => (
        <div className='table-cell'>
          {getPlayingTimeIcon(row.availability_status, 18, 'dark')}
        </div>
      ),
    },
    {
      key: 'skill_index',
      width: playerTableOverallIndexColumnWidth + (skillIndexPaddingRight ?? 0),
      orderByKey: 'skill_index',
      headerPaddingRight: skillIndexPaddingRight,
      cell: ({ row }: PlayerTableColumnCellProps) => (
        <div className='table-cell' style={{ paddingRight: skillIndexPaddingRight }}>
          <IndexCell
            playerOverview={row}
            property='skill_index'
            positionGroup={'OVERALL'}
            showConfidence={showIndexConfidences}
          />
        </div>
      ),
    },
  ];


  if (hasSkillcorner) {
    dataColumns.push({
      key: 'physical_index',
      width: playerTableOverallIndexColumnWidth + (physicalIndexPaddingRight ?? 0),
      orderByKey: 'physical_index',
      headerPaddingRight: physicalIndexPaddingRight,
      cell: ({ row }: PlayerTableColumnCellProps) => (
        <div className='table-cell' style={{ paddingRight: physicalIndexPaddingRight }}>
          <IndexCell
            playerOverview={row}
            property='physical_index'
            positionGroup={'OVERALL'}
            showConfidence={showIndexConfidences}
          />
        </div>
      ),
    },);
  }

  // if (!anyRoleIsSelected && !hasSkillcorner) {
  //   dataColumns.push({
  //     key: 'confidence',
  //     width: playerTableConfidenceColumnWidth + playerTablePadding,
  //     headerPaddingRight: playerTablePadding,
  //     cell: ({ row }: PlayerTableColumnCellProps) => (
  //       <div className='table-cell' style={{ paddingRight: playerTablePadding }}>
  //         <ConfidenceCell playerOverview={row} positionGroup={'OVERALL'} />
  //       </div>
  //     ),
  //   });
  // }

  return dataColumns;
};


export const getPlayerTableRoleIndexColumns = (
  selectedRoles: string[] | undefined,
  showIndexConfidences: boolean,
) => {

  const dataColumns: PlayerTableColumn[] = [];

  if (selectedRoles) {
    selectedRoles.forEach((roleId, index) => {

      const paddingRight = index === (selectedRoles.length - 1) ? playerTablePadding : undefined;

      dataColumns.push({
        key: roleId,
        width: playerTableRoleIndexColumnWidth + (paddingRight ?? 0),
        roleId: roleId,
        orderByKey: roleId,
        headerPaddingRight: paddingRight,
        cell: ({ row, positionGroup }: PlayerTableColumnCellProps) => (
          <div className='table-cell' style={{ paddingRight }}>
            <IndexCell
              playerOverview={row}
              property={roleId}
              positionGroup={positionGroup ?? 'OVERALL'}
              showConfidence={showIndexConfidences}
            />
          </div>
        ),
      });
    });
  }

  return dataColumns;
};
